import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ShoppingBag, Truck, Plus, X, Copy, ScanLine, BadgeCheck, Pencil, Send, ArrowDownUp, ExternalLink, ImageDown, Loader2 } from "lucide-react";
import api, { planWeek } from "../lib/api";
import { useSearchParams, useNavigate } from "react-router-dom";
import ShopPrefs from "../components/ShopPrefs";
import { useAuth } from "../context/AuthContext";
import MobileShell from "../components/MobileShell";
import { GROCERIES_IMG } from "../lib/millie";
import HandoffSheet from "../components/HandoffSheet";
import StorePicker from "../components/StorePicker";
import ScanListView from "../components/ScanListView";
import SendToAppSheet from "../components/SendToAppSheet";
import FixMatchModal from "../components/FixMatchModal";

const CAT_EMOJI = {
  "Fruit & Veg": "🥦", "Meat & Fish": "🍗", Dairy: "🧀",
  Bakery: "🍞", Pantry: "🥫", Frozen: "🧊", Other: "🛒", "Pet Food": "🐾", Household: "🧻",
};

export default function ShoppingList() {
  const { activeId, activeHousehold, loadHouseholds, user } = useAuth();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const cur = activeHousehold?.currency || "A$";
  const [extra, setExtra] = useState("");
  const [handoff, setHandoff] = useState(false);
  const defaultShop = (activeHousehold?.supermarkets || []).some((s) => /coles/i.test(s)) && !(activeHousehold?.supermarkets || []).some((s) => /wool/i.test(s)) ? "coles" : "woolies";
  const [shop, setShopState] = useState(() => localStorage.getItem("sorted_shop") || user?.shop_pref || defaultShop);
  const [mode, setModeState] = useState("best");
  const [store, setStore] = useState(null);
  const [matching, setMatching] = useState(false);
  const [scan, setScan] = useState(false);
  const [sendApp, setSendApp] = useState(false);
  const [fixing, setFixing] = useState(null);
  const [aisle, setAisle] = useState(() => localStorage.getItem("sorted_aisle") === "1");
  const toggleAisle = () => { const v = !aisle; setAisle(v); localStorage.setItem("sorted_aisle", v ? "1" : "0"); };
  const label = shop === "coles" ? "Coles" : "Woolworths";
  const setShop = async (k) => {
    setShopState(k); localStorage.setItem("sorted_shop", k); setStore(null);
    try { await api.put("/shopping/shop-pref", { shop: k }); } catch { /* local pref still applies */ }
  };
  const setMode = async (m) => {
    setModeState(m);
    try { await api.put("/trolley/pick-mode", { mode: m }); setStore(null); } catch { toast.error("Couldn't save that"); }
  };
  const shops = activeHousehold?.supermarkets?.length ? activeHousehold.supermarkets.join(" · ") : "your local shops";

  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isNext = params.get("week") === "next";
  planWeek.value = isNext ? "next" : "current";
  useEffect(() => () => { planWeek.value = "current"; }, []);
  const setWeek = (w) => { setParams(w === "next" ? { week: "next" } : {}); setList(null); };

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/shopping/${activeId}`);
      setList(data);
    } finally {
      setLoading(false);
    }
  }, [activeId, isNext]);

  const listSig = list?.categories?.map((c) => c.items.map((i) => i.name).join(",")).join("|") || "";
  const loadStore = useCallback(async () => {
    if (!activeId || !listSig) { setStore(null); return; }
    setMatching(true);
    try {
      const { data } = await api.get(`/shopping/${activeId}/store`, { params: { shop } });
      setStore(data); setModeState(data.mode || "best");
    } catch { setStore(null); }
    finally { setMatching(false); }
  }, [activeId, listSig, shop, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadStore(); }, [loadStore]);

  const orderedCats = (() => {
    if (!list?.categories) return [];
    if (!aisle || !store?.aisle_order) return list.categories;
    const rank = Object.fromEntries(store.aisle_order.map((c, i) => [c, i]));
    return [...list.categories].sort((a, b) => (rank[a.category] ?? 99) - (rank[b.category] ?? 99));
  })();
  const unchecked = store?.categories?.reduce((n, c) => n + c.items.filter((i) => !i.checked).length, 0) || 0;
  const [imgBusy, setImgBusy] = useState(false);
  const saveImage = async () => {
    setImgBusy(true);
    try {
      const { data } = await api.get(`/shopping/${activeId}/store-image`, { params: { shop }, responseType: "blob" });
      const file = new File([data], `sorted-${shop}-list.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `${label} shopping list` });
      } else {
        const a = document.createElement("a"); a.href = URL.createObjectURL(file); a.download = file.name; a.click(); URL.revokeObjectURL(a.href);
        toast.success(`Saved — upload it with the ${label} app's scan-a-list feature`);
      }
    } catch (err) { if (err?.name !== "AbortError") toast.error("Couldn't make the image"); }
    finally { setImgBusy(false); }
  };
  const productOf = (name) => store?.categories?.flatMap((c) => c.items).find((i) => i.name === name);
  const copyStoreList = async () => {
    if (!store?.text) return;
    try { await navigator.clipboard.writeText(store.text); toast.success(`List copied — paste it into the ${label} app`); } catch { toast.error("Couldn't copy"); }
  };

  useEffect(() => { load(); }, [load]);

  const checked = new Set(list?.checked || []);

  const toggle = async (name) => {
    // optimistic
    setList((prev) => {
      const next = new Set(prev.checked);
      next.has(name) ? next.delete(name) : next.add(name);
      const cats = prev.categories.map((c) => ({
        ...c, items: c.items.map((it) => (it.name === name ? { ...it, checked: next.has(name) } : it)),
      }));
      return { ...prev, checked: [...next], categories: cats };
    });
    try { await api.post("/shopping/toggle", { household_id: activeId, item: name }); } catch { load(); }
  };

  const addExtra = async () => {
    const v = extra.trim();
    if (!v) return;
    setExtra("");
    try { await api.post("/shopping/extra", { household_id: activeId, item: v }); await loadHouseholds(); await load(); toast.success(`${v} added to the shop`); }
    catch { toast.error("Couldn't add that one, sorry"); }
  };
  const removeExtra = async (name) => {
    try { await api.post("/shopping/extra", { household_id: activeId, item: name, remove: true }); await loadHouseholds(); await load(); }
    catch { toast.error("Couldn't remove that"); }
  };

  const totalItems = list?.categories?.reduce((s, c) => s + c.items.length, 0) || 0;
  const doneItems = list?.categories?.reduce((s, c) => s + c.items.filter((i) => checked.has(i.name)).length, 0) || 0;

  return (
    <MobileShell>
      <div className="relative h-40">
        <img src={GROCERIES_IMG} alt="Fresh groceries" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-4 left-6">
          <h1 className="font-heading text-3xl font-bold text-sorted-ink drop-shadow-sm">Shopping List</h1>
          <p className="text-sorted-ink/80 font-semibold text-sm" data-testid="shopping-shops">Built from {isNext ? "next week" : "your week"} · {shops}</p>
        </div>
      </div>

      <div className="px-6 -mt-2 space-y-3">
        <div className="bg-white rounded-full p-1 flex border-2 border-[#FDECEF]" data-testid="shopping-week-toggle">
          {[["current", "This week"], ["next", "Next week"]].map(([k, l]) => (
            <button key={k} onClick={() => setWeek(k)} data-testid={`shopping-week-${k}`}
              className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${(k === "next") === isNext ? "bg-sorted-ink text-white" : "text-sorted-slate"}`}>{l}</button>
          ))}
        </div>
        <ShopPrefs />
        <StorePicker shop={shop} setShop={setShop} mode={mode} setMode={setMode} matching={matching} />
      </div>

      <div className="px-6 mt-3">
        {list && totalItems > 0 && (
          <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] p-4 shadow-soft flex items-center justify-between" data-testid="shopping-summary">
            <div>
              <p className="text-xs font-bold text-sorted-slate">Estimated total</p>
              <p className="font-heading text-2xl font-bold text-sorted-ink">{cur}{Number(list.estimated_total).toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold text-sorted-slate">{doneItems}/{totalItems} in the trolley</p>
              <div className="w-24 h-2 bg-muted rounded-full mt-1 overflow-hidden">
                <div className="h-full bg-sorted-peach rounded-full transition-all" style={{ width: `${totalItems ? (doneItems / totalItems) * 100 : 0}%` }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-6 mt-6 space-y-6">
        {loading ? (
          [0, 1].map((i) => <div key={i} className="h-40 rounded-3xl bg-white/60 animate-pulse" />)
        ) : totalItems === 0 ? (
          <div className="text-center pt-8">
            <ShoppingBag size={40} strokeWidth={2} className="mx-auto text-sorted-slate" />
            <p className="text-sorted-slate mt-3 max-w-xs mx-auto">{isNext ? "Next week isn't planned yet — plan it and I'll have this list ready before you shop." : "Plan a week first and I'll sort your shopping list for you — too easy."}</p>
            {isNext && <button onClick={() => navigate("/meals?week=next")} data-testid="shopping-plan-next" className="mt-4 bg-sorted-rose text-white font-bold px-5 py-3 rounded-full">Plan next week</button>}
          </div>
        ) : (
          orderedCats.map((cat, ci) => (
            <div key={cat.category} data-testid={`shop-cat-${cat.category}`}>
              <h2 className="font-heading text-lg font-semibold text-sorted-ink mb-2 flex items-center gap-2">
                <span>{CAT_EMOJI[cat.category] || "🛒"}</span> {cat.category}
                {aisle && <span className="ml-auto text-[10px] font-bold text-sorted-slate bg-white rounded-full px-2 py-0.5 border border-[#FDECEF]" data-testid={`aisle-badge-${cat.category}`}>Aisle stop {ci + 1}</span>}
              </h2>
              <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft overflow-hidden">
                {cat.items.map((it, idx) => {
                  const on = checked.has(it.name);
                  return (
                    <button
                      key={it.name}
                      onClick={() => toggle(it.name)}
                      data-testid={`shop-item-${it.name}`}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${idx ? "border-t border-[#FFF3F0]" : ""} ${on ? "bg-muted/60" : "hover:bg-muted/40"}`}
                    >
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${on ? "bg-sorted-peach border-sorted-peach" : "border-sorted-blush"}`}>
                        {on && <Check size={14} strokeWidth={3.5} className="text-sorted-ink" />}
                      </span>
                      {(() => { const p = productOf(it.name); const hasP = p && p.product && p.product !== it.name; return (
                        <span className="flex-1 min-w-0">
                          <span className={`block font-semibold leading-tight ${on ? "line-through text-sorted-slate" : "text-sorted-ink"}`} data-testid={`shop-product-${it.name}`}>
                            {hasP ? p.product : it.name}
                            {p?.verified && <BadgeCheck size={13} strokeWidth={2.5} className="inline ml-1 text-emerald-600 -mt-0.5" aria-label="Matched in store" />}
                            {p?.fixed && <Pencil size={11} strokeWidth={2.5} className="inline ml-1 text-sorted-rose -mt-0.5" aria-label="Your correction" />}
                          </span>
                          {(hasP || it.quantity || it.unit) && (
                            <span className="block text-[11px] font-semibold text-sorted-slate mt-0.5">{hasP ? `for ${it.name}` : ""}{(it.quantity || it.unit) ? `${hasP ? " · " : ""}${it.quantity} ${it.unit}`.replace(/\s+$/, "") : ""}</span>
                          )}
                        </span>
                      ); })()}
                      {productOf(it.name)?.qty > 1 && <span className="text-xs font-bold text-sorted-ink bg-sorted-peach/60 rounded-full px-2 py-0.5" data-testid={`shop-qty-${it.name}`}>×{productOf(it.name).qty}</span>}
                      {productOf(it.name)?.product_url && (
                        <a href={productOf(it.name).product_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`shop-open-${it.name}`} aria-label={`Open ${it.name} at ${label}`}
                          className="w-7 h-7 rounded-full bg-[#DDEBE1] text-emerald-700 flex items-center justify-center shrink-0"><ExternalLink size={12} strokeWidth={2.5} /></a>
                      )}
                      {productOf(it.name) && (
                        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setFixing(productOf(it.name)); }} data-testid={`shop-fix-${it.name}`} aria-label={`Fix match for ${it.name}`}
                          className="w-7 h-7 rounded-full bg-muted text-sorted-slate flex items-center justify-center shrink-0"><Pencil size={12} strokeWidth={2.5} /></span>
                      )}
                      {it.extra && cat.category === "Household" && (
                        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); removeExtra(it.name); }} data-testid={`remove-extra-${it.name}`} aria-label={`Remove ${it.name}`}
                          className="w-7 h-7 rounded-full bg-muted text-sorted-slate flex items-center justify-center"><X size={13} strokeWidth={3} /></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}

        <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft p-4" data-testid="household-extras">
          <p className="font-heading font-semibold text-sorted-ink">Need anything else?</p>
          <p className="text-xs text-sorted-slate mb-3">Loo paper, dishwashing tabs, dog food — chuck it on the list.</p>
          <div className="flex items-center gap-2">
            <input data-testid="extra-item-input" value={extra} onChange={(e) => setExtra(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addExtra()}
              placeholder="e.g. Toilet paper" className="flex-1 min-w-0 bg-muted rounded-2xl px-4 py-3 font-medium text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach" />
            <button onClick={addExtra} data-testid="extra-item-add" aria-label="Add item" className="w-11 h-11 rounded-full bg-sorted-peach text-sorted-ink flex items-center justify-center shrink-0"><Plus size={20} strokeWidth={2.5} /></button>
          </div>
        </div>

        {totalItems > 0 && store?.text && (
          <button onClick={() => setSendApp(true)} data-testid="send-to-app" className="w-full text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft" style={{ background: shop === "coles" ? "#E01A22" : "#3E8E41" }}>
            <Send size={18} strokeWidth={2.5} /> Send {unchecked} products to the {label} app
          </button>
        )}
        {totalItems > 0 && store?.text && (
          <div className="grid grid-cols-4 gap-2" data-testid="store-list-actions">
            <button onClick={toggleAisle} data-testid="toggle-aisle" className={`border-2 font-bold py-3 rounded-full flex items-center justify-center gap-1 text-xs ${aisle ? "bg-sorted-ink border-sorted-ink text-white" : "bg-white border-[#FDECEF] text-sorted-ink"}`}><ArrowDownUp size={14} strokeWidth={2.5} /> Aisles</button>
            <button onClick={copyStoreList} data-testid="copy-store-list" className="bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-1 text-xs"><Copy size={14} strokeWidth={2.5} /> Copy</button>
            <button onClick={() => setScan(true)} data-testid="open-scan-view" className="bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-1 text-xs"><ScanLine size={14} strokeWidth={2.5} /> Scan</button>
            <button onClick={saveImage} disabled={imgBusy} data-testid="save-list-image" className="bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-1 text-xs disabled:opacity-60">{imgBusy ? <Loader2 size={14} className="animate-spin" /> : <ImageDown size={14} strokeWidth={2.5} />} Image</button>
          </div>
        )}
        {totalItems > 0 && (
          <button
            onClick={() => setHandoff(true)}
            data-testid="order-supermarket"
            className="w-full bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3.5 rounded-full flex items-center justify-center gap-2"
          >
            <Truck size={17} strokeWidth={2.5} /> Millie's 1-tap trolley helper
          </button>
        )}
      </div>
      <HandoffSheet open={handoff} onClose={() => setHandoff(false)} activeId={activeId} defaultShop={shop} cur={cur} onChanged={load} />
      <SendToAppSheet open={sendApp} onClose={() => setSendApp(false)} shop={shop} text={store?.text || ""} count={unchecked} onAdvanced={() => { setSendApp(false); navigate("/helper-setup"); }} />
      <FixMatchModal item={fixing} shop={shop} label={label} activeId={activeId} onClose={() => setFixing(null)} onSaved={() => { setStore(null); loadStore(); }} />
      <ScanListView open={scan} onClose={() => setScan(false)} label={label} categories={store?.categories || []} text={store?.text || ""} />
    </MobileShell>
  );
}
